/**
 * Reducer for InpaintTab component state management.
 */

import type { ImageResult } from '@/types';

// ============================================================================
// State
// ============================================================================

export interface InpaintResult {
  images: ImageResult[];
  processingTime: number;
}

export interface InpaintState {
  imageBase64: string;
  imageFile: File | null;
  maskBase64: string;
  prompt: string;
  negativePrompt: string;
  showNegativePrompt: boolean;
  selectedStyles: string[];
  model: string | undefined;
  isProcessing: boolean;
  error: string | null;
  result: InpaintResult | null;
}

export const initialState: InpaintState = {
  imageBase64: '',
  imageFile: null,
  maskBase64: '',
  prompt: '',
  negativePrompt: '',
  showNegativePrompt: false,
  selectedStyles: [],
  model: undefined,
  isProcessing: false,
  error: null,
  result: null,
};

// ============================================================================
// Actions
// ============================================================================

export type InpaintAction =
  | { type: 'SET_IMAGE'; payload: { base64: string; file: File } }
  | { type: 'REMOVE_IMAGE' }
  | { type: 'SET_MASK'; payload: string }
  | { type: 'SET_PROMPT'; payload: string }
  | { type: 'SET_NEGATIVE_PROMPT'; payload: string }
  | { type: 'SHOW_NEGATIVE_PROMPT' }
  | { type: 'HIDE_NEGATIVE_PROMPT' }
  | { type: 'SET_SELECTED_STYLES'; payload: string[] }
  | { type: 'SET_MODEL'; payload: string | undefined }
  | { type: 'SET_PROCESSING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_RESULT'; payload: InpaintResult | null }
  | { type: 'RESET_RESULT' };

// ============================================================================
// Reducer
// ============================================================================

export function inpaintReducer(state: InpaintState, action: InpaintAction): InpaintState {
  switch (action.type) {
    case 'SET_IMAGE':
      return {
        ...state,
        imageBase64: action.payload.base64,
        imageFile: action.payload.file,
        error: null,
      };

    case 'REMOVE_IMAGE':
      return {
        ...state,
        imageBase64: '',
        imageFile: null,
        maskBase64: '',
      };

    case 'SET_MASK':
      return { ...state, maskBase64: action.payload };

    case 'SET_PROMPT':
      return { ...state, prompt: action.payload };

    case 'SET_NEGATIVE_PROMPT':
      return { ...state, negativePrompt: action.payload };

    case 'SHOW_NEGATIVE_PROMPT':
      return { ...state, showNegativePrompt: true };

    case 'HIDE_NEGATIVE_PROMPT':
      return { ...state, showNegativePrompt: false, negativePrompt: '' };

    case 'SET_SELECTED_STYLES':
      return { ...state, selectedStyles: action.payload };

    case 'SET_MODEL':
      return { ...state, model: action.payload };

    case 'SET_PROCESSING':
      return { ...state, isProcessing: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload };

    case 'SET_RESULT':
      return { ...state, result: action.payload };

    case 'RESET_RESULT':
      return { ...state, result: null, error: null };

    default:
      return state;
  }
}
